import React from 'react';
import { Building2, Award, Heart, TrendingUp, CheckCircle2, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';

const WhyCollaborate = () => {
  const benefits = [
    {
      icon: <Building2 className="w-6 h-6" />,
      title: 'Responsabilidad Social Corporativa',
      description: 'Fortalezca la imagen de su empresa con un compromiso auténtico con el desarrollo deportivo nacional.',
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: 'Reconocimiento Público',
      description: 'Visibilidad de su marca en eventos deportivos, medios de comunicación y nuestras plataformas digitales.',
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: 'Impacto Social Medible',
      description: 'Reportes transparentes sobre el uso de fondos y el impacto directo en la vida de los atletas.',
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Beneficios Fiscales',
      description: 'Deducciones impositivas aplicables según la legislación argentina para donaciones a fundaciones.',
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Networking Corporativo',
      description: 'Acceso a una red de empresas comprometidas con el desarrollo social y deportivo del país.',
    },
    {
      icon: <CheckCircle2 className="w-6 h-6" />,
      title: 'Transparencia Total',
      description: 'Reportes periódicos, auditorías independientes y acceso a resultados de cada programa financiado.',
    },
  ];

  const collaborationLevels = [
    {
      name: 'Sponsor Bronce',
      amount: 'Desde $500.000/mes',
      benefits: ['Logo en materiales', 'Reporte trimestral', 'Certificado de donación'],
      color: 'from-amber-600 to-amber-700',
    },
    {
      name: 'Sponsor Plata',
      amount: 'Desde $1.500.000/mes',
      benefits: ['Todo lo anterior', 'Mención en redes sociales', 'Invitación a eventos', 'Reporte mensual'],
      color: 'from-gray-400 to-gray-500',
      featured: true,
    },
    {
      name: 'Sponsor Oro',
      amount: 'Desde $3.000.000/mes',
      benefits: ['Todo lo anterior', 'Logo destacado en eventos', 'Naming de programas', 'Encuentros con atletas'],
      color: 'from-yellow-500 to-yellow-600',
    },
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contacto');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="colaborar" className="py-24 bg-gradient-to-b from-white to-sky-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-sky-600 font-semibold text-sm uppercase tracking-wide mb-2 block">
            Oportunidad de Colaboración
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Por Qué Colaborar con Nosotros
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Su empresa puede ser parte del cambio y obtener beneficios significativos
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto mb-20">
          {benefits.map((benefit, index) => (
            <Card
              key={index}
              className="p-6 border-2 border-gray-100 hover:border-sky-400 hover:shadow-xl transition-all duration-300"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-sky-400 to-sky-600 rounded-xl flex items-center justify-center text-white mb-4">
                {benefit.icon}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
            </Card>
          ))}
        </div>

        {/* Collaboration Levels */}
        <div className="max-w-6xl mx-auto mb-16">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">Niveles de Colaboración</h3>
          <div className="grid md:grid-cols-3 gap-8">
            {collaborationLevels.map((level, index) => (
              <Card
                key={index}
                className={`p-8 border-2 hover:shadow-2xl transition-all duration-300 ${
                  level.featured
                    ? 'border-sky-400 shadow-xl scale-105 relative'
                    : 'border-gray-200 hover:border-sky-300'
                }`}
              >
                {level.featured && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-sky-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Más Popular
                    </span>
                  </div>
                )}
                <div className={`w-16 h-16 bg-gradient-to-br ${level.color} rounded-2xl flex items-center justify-center text-white text-2xl font-bold mb-6`}>
                  {index + 1}
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-2">{level.name}</h4>
                <p className="text-3xl font-bold text-sky-600 mb-6">{level.amount}</p>
                <ul className="space-y-3 mb-8">
                  {level.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">{benefit}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={scrollToContact}
                  className={`w-full ${
                    level.featured
                      ? 'bg-sky-600 hover:bg-sky-700'
                      : 'bg-gray-800 hover:bg-gray-900'
                  } text-white transition-colors`}
                >
                  Contactar
                </Button>
              </Card>
            ))}
          </div>
        </div>

        {/* Image and CTA */}
        <div className="max-w-6xl mx-auto">
          <div className="relative overflow-hidden rounded-2xl shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1542323228-002ac256e7b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjBzcG9uc29yc2hpcCUyMHBhcnRuZXJzaGlwfGVufDB8fHx8MTc4MjY4MjQyMnww&ixlib=rb-4.1.0&q=85"
              alt="Colaboración corporativa"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-sky-900/95 to-blue-900/90 flex items-center">
              <div className="container mx-auto px-8">
                <h3 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                  Juntos Construimos Campeones
                </h3>
                <p className="text-xl text-sky-100 mb-8 max-w-2xl">
                  Su inversión en nuestra fundación es una inversión en el futuro del deporte argentino.
                  Contáctenos para conocer cómo su empresa puede marcar la diferencia.
                </p>
                <Button
                  onClick={scrollToContact}
                  size="lg"
                  className="bg-white text-sky-900 hover:bg-sky-50 text-lg px-8 py-6 transition-colors"
                >
                  Iniciar Colaboración
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyCollaborate;
